import {useCurrentMaterialStorages, isStorageWithMaterialGuard} from '@domain/material-storage'
import {MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {HPTextField, HPToggleButton} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {MenuItem, Stack, SvgIcon} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {createSearchParams, useLocation, useNavigate, useParams} from 'react-router'

import {CementIcon} from '../assets'
import {ReactComponent as SiloIcon} from '../assets/icons/SilosIcon.svg'
import {FEATURE_NAMESPACE} from '../consts'
import {useSearchParams, useCurrentStoredMaterialTypesQuery} from '../hooks'
import {useStorageLevelsRoute} from '../providers'
import {getStockSubPath} from '../utils'

const validateMaterialType = (input: string): input is MaterialType => {
  return Object.values(MaterialType).includes(input as MaterialType) ? true : false
}

export const StockActionHeader: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {plantId: plantCode} = useUpmPlantContext()
  const {'*': relativePath = ''} = useParams()
  const {getStockPath} = useStorageLevelsRoute()

  // Use relativePath if available (Suite), otherwise use full pathname (HCEM)
  const pathForDetection = relativePath || location.pathname
  const stockSubPath = getStockSubPath(pathForDetection)

  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [obtainedParams, setMaterialType] = useSearchParams('materialType')
  const obtainedMaterialType = obtainedParams.materialType
  if (obtainedMaterialType === null) {
    throw new Error('BUG: materialType search param should be set before rendering this component')
  }
  if (!validateMaterialType(obtainedMaterialType)) {
    throw new Error(`materialType value "${obtainedMaterialType}" is incorrect`)
  }
  const {data: storagesWithMaterial} = useCurrentMaterialStorages({
    plantId: plantCode,
    options: {select: (storages) => storages.filter(isStorageWithMaterialGuard), throwOnError: true}
  })
  if (!storagesWithMaterial) {
    throw new Error('BUG: storages with material should be loaded to render this component')
  }
  const {data: availableMaterialTypes} = useCurrentStoredMaterialTypesQuery()

  if (!availableMaterialTypes) {
    throw new Error('BUG: material storage history should be loaded to render this component')
  }

  if (!stockSubPath) {
    return null
  }

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <HPTextField
        sx={{
          width: ({spacing}) => spacing(28)
        }}
        variant="outlined"
        fullWidth
        select
        label={t('stock.materialType')}
        value={obtainedMaterialType}
        onChange={(e) => {
          const newMaterialType = e.target.value
          setMaterialType({materialType: newMaterialType})
        }}
        backgroundMode="onLight"
        {...dataTestId('material_type_select')}
      >
        {availableMaterialTypes.map((type) => (
          <MenuItem key={type} value={type} {...dataTestId(`${type}_select_item`)}>
            {t(`stock.materials.materialTypes.${type}`)}
          </MenuItem>
        ))}
      </HPTextField>
      <HPToggleButton
        value={stockSubPath}
        onChange={(subPath) => {
          const pathname = getStockPath(subPath as 'silos' | 'materials')

          void navigate({
            pathname,
            search: createSearchParams({materialType: obtainedMaterialType}).toString()
          })
        }}
        backgroundMode="light"
        color="primary"
        sx={{
          '& .MuiToggleButtonGroup-grouped.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }
        }}
        options={[
          {
            value: 'silos',
            title: t('common.silo'),
            icon: (
              <SvgIcon
                component={SiloIcon}
                viewBox="0 0 14 24"
                sx={{'& path': {fill: 'currentColor'}}}
              />
            ),
            dataTestId: 'silo_view_button'
          },
          {
            value: 'materials',
            title: t('common.material'),
            icon: <CementIcon sx={{fontSize: ({spacing}) => spacing(2)}} />,
            dataTestId: 'material_view_button'
          }
        ]}
        {...dataTestId('stock_view_toggle')}
      />
    </Stack>
  )
}
